import React, { useRef, useState } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";

export default function ReviewsSlider({ reviews, dark = false }) {
  const scrollContainerRef = useRef(null);
  const [expandedReviews, setExpandedReviews] = useState({});

  if (!reviews || reviews.length === 0) return null;

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const { scrollLeft, clientWidth } = scrollContainerRef.current;
      const scrollAmount = clientWidth * 0.75; // Scroll 75% of container width
      scrollContainerRef.current.scrollTo({
        left: direction === "left" ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const toggleExpand = (id) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className={`w-full py-16 border-y ${
      dark 
        ? "bg-neutral-900/30 border-neutral-800/50" 
        : "bg-white border-neutral-200/60"
    }`}>
      {/* Header & Navigation Row - Aligned with the container line */}
      <div className="max-w-[1400px] mx-auto px-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
        <div className="text-left space-y-3">
          <p className={`text-3xl md:text-4xl font-light leading-tight ${
            dark ? "text-white" : "text-neutral-950"
          }`}>
            Hvad klienterne siger
          </p>
        </div>
        
        {/* Navigation Arrows aligned with the container edge */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => scroll("left")}
            className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all cursor-pointer ${
              dark 
                ? "bg-neutral-900 border-neutral-800 text-neutral-300 hover:text-brand-400 hover:border-brand-500/50 hover:shadow-lg hover:shadow-brand-500/5" 
                : "bg-white border-neutral-200 text-neutral-700 hover:text-brand-600 hover:border-brand-300 hover:shadow-md"
            }`}
            aria-label="Forrige udtalelse"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => scroll("right")}
            className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all cursor-pointer ${
              dark 
                ? "bg-neutral-900 border-neutral-800 text-neutral-300 hover:text-brand-400 hover:border-brand-500/50 hover:shadow-lg hover:shadow-brand-500/5" 
                : "bg-white border-neutral-200 text-neutral-700 hover:text-brand-600 hover:border-brand-300 hover:shadow-md"
            }`}
            aria-label="Næste udtalelse"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Slider Container - Now strictly following the container line */}
      <div className="max-w-[1400px] mx-auto px-6">
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto scrollbar-none snap-x snap-mandatory scroll-smooth py-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {reviews.map((review, idx) => {
            const reviewId = review.id || idx;
            const isExpanded = expandedReviews[reviewId];
            // Simple check to see if content is long enough to warrant a "Læs mere" button
            const isLong = review.content && review.content.length > 180;

            return (
              <div
                key={reviewId}
                className={`w-[290px] sm:w-[360px] md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] shrink-0 border rounded-2xl p-6 md:p-8 flex flex-col justify-between snap-start transition-all duration-300 ${
                  dark 
                    ? "bg-neutral-900/60 border-neutral-800/80 text-neutral-300 hover:border-brand-500/30 shadow-sm hover:shadow-md" 
                    : "bg-white border-neutral-100 text-neutral-700 hover:border-brand-200 shadow-md hover:shadow-xl"
                }`}
              >
                <div>
                  <Quote className={`w-8 h-8 mb-4 ${
                    dark ? "text-brand-400/20" : "text-brand-500/10"
                  }`} />
                  {review.title && (
                    <h4 className={`text-base md:text-lg font-medium mb-3 leading-snug ${
                      dark ? "text-white" : "text-neutral-900"
                    }`}>
                      {review.title}
                    </h4>
                  )}
                  <div
                    className={`text-sm md:text-base leading-relaxed font-light italic transition-all duration-300 ${
                      dark ? "text-neutral-300" : "text-neutral-700"
                    } ${isExpanded ? "" : "line-clamp-4"}`}
                    dangerouslySetInnerHTML={{ __html: review.content }}
                  />
                  {isLong && (
                    <button
                      onClick={() => toggleExpand(reviewId)}
                      className="mt-3 text-xs font-semibold uppercase tracking-wider text-brand-600 hover:text-brand-700 transition-colors cursor-pointer block"
                    >
                      {isExpanded ? "Vis mindre" : "Læs mere"}
                    </button>
                  )}
                </div>
                
                <div className={`mt-6 pt-6 border-t flex items-center justify-between ${
                  dark ? "border-neutral-800/60" : "border-neutral-100"
                }`}>
                  <span className={`font-display text-base font-medium tracking-wide ${
                    dark ? "text-brand-300" : "text-neutral-900"
                  }`}>
                    {review.author}
                  </span>
                  <span className={`text-xs tracking-wider uppercase ${
                    dark ? "text-neutral-500" : "text-neutral-400"
                  }`}>
                    Klientudtalelse
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
